import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { gatewayPaymentAPI } from '@matix/api-client';
import { PAYMENT_GATEWAYS, type PaymentGatewayCode } from '@matix/types';
import { api, type PaidSummary, type TapChargeResult } from '../../lib/api';
import { fmtMoney } from '../../lib/currency';
import { useLang } from '../../lib/lang';
import { useErrMsg } from '../../lib/errors';

/**
 * Take the balance of an appointment on a card, at reception.
 *
 * This is the cashier's `TapPaymentDialog` shape — open a charge, show the
 * hosted page, poll every three seconds until CAPTURED — with one deliberate
 * difference: the ORDER IS NOT BUILT HERE. The till hands Tap a complete
 * `CreateOrderRequest` because the till owns the cart; an appointment is priced
 * from rows the server already holds, and the deposit that may settle part of
 * it is a fact only the server can be trusted with. So `POST
 * /appointments/sessions/:id/tap-charge` prices the invoice and stores the
 * draft, and this dialog only watches the money arrive.
 *
 * Polling rather than webhooks, exactly as the till does: it keeps the timing
 * in the screen's hands and exposes no public endpoint. The poll is the
 * ordinary `/payments/tap/charges/:id`, which is the one that writes the
 * receipt and stamps the appointment — a second finaliser written for this app
 * would be a second chance at the duplicate-receipt bug that endpoint was
 * hardened against.
 */

const POLL_INTERVAL_MS = 3_000;

export interface TapChargeDialogProps {
  /** Which hosted gateway takes the balance. */
  gateway: PaymentGatewayCode;
  sessionId: number;
  taxPercent: number;
  /** What the invoice will be, for the summary line. */
  total: number;
  /** Settled by the online deposit, for the same line. */
  depositCredit: number;
  onClose: () => void;
  /** Fired once the gateway has captured AND the receipt exists. */
  onPaid: (result: PaidSummary) => void;
}

type Phase = 'creating' | 'waiting' | 'succeeded' | 'failed';

export function TapChargeDialog({
  gateway,
  sessionId,
  taxPercent,
  total,
  depositCredit,
  onClose,
  onPaid,
}: TapChargeDialogProps): JSX.Element {
  const { t } = useLang();
  const errMsg = useErrMsg();

  const [phase, setPhase] = useState<Phase>('creating');
  const [charge, setCharge] = useState<TapChargeResult | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [polls, setPolls] = useState(0);

  // Refs so the interval never reads a stale closure — the same reason the
  // till's dialog keeps them.
  const chargeIdRef = useRef<string | null>(null);
  const phaseRef = useRef<Phase>('creating');
  const inFlightRef = useRef(false);
  const finish = (p: Phase): void => {
    phaseRef.current = p;
    setPhase(p);
  };

  // ── 1. Open the charge ──────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const opened = await api.tapCharge(sessionId, { tax_percentage: taxPercent, complete: true, gateway });
        if (cancelled) return;
        chargeIdRef.current = opened.charge_id;
        setCharge(opened);
        finish('waiting');
        // Rendered whatever the terminal mode says: a receptionist with no
        // card terminal on the desk hands the customer the QR, and one with an
        // iframe still benefits from having something to point a phone at if
        // the frame is blocked.
        try {
          setQrDataUrl(await QRCode.toDataURL(opened.redirect_url, { width: 240, margin: 1 }));
        } catch {
          /* the link below still works */
        }
      } catch (e) {
        if (cancelled) return;
        setError(errMsg(e));
        finish('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // ── 2. Poll until the gateway settles it ────────────────────────────────
  useEffect(() => {
    if (phase !== 'waiting') return undefined;
    const tick = async (): Promise<void> => {
      const id = chargeIdRef.current;
      // One request at a time. A slow gateway must not stack pollers up, each
      // of which could try to finalise the same charge.
      if (!id || inFlightRef.current || phaseRef.current !== 'waiting') return;
      inFlightRef.current = true;
      try {
        const status = await gatewayPaymentAPI.pollCharge(id);
        setPolls((n) => n + 1);
        if (status.status === 'captured' && status.order) {
          finish('succeeded');
          // The poll returns the ORDER, not the conversion envelope — the
          // appointment was stamped server-side inside that same request, and
          // the caller refetches the session either way.
          onPaid({
            order_id: status.order.order_id,
            entry_no: status.order.entry_no,
            net_value: status.order.totals?.net_value ?? total,
            // A card is charged for exactly what is owed; there is no change.
            change: 0,
            deposit_applied: depositCredit,
          });
        } else if (status.status === 'failed' || status.status === 'cancelled') {
          setError(
            status.failure_reason ||
              t({ en: 'The card payment did not go through.', ar: 'لم تتم عملية الدفع بالبطاقة.' }),
          );
          finish('failed');
        }
      } catch (e) {
        // A transient gateway error is not a failed payment: keep polling and
        // leave the screen as it is.
        void e;
      } finally {
        inFlightRef.current = false;
      }
    };
    void tick();
    const handle = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  async function cancel(): Promise<void> {
    const id = chargeIdRef.current;
    // Cancelled locally only — Tap's own page may still be open in front of the
    // customer, and a charge they complete afterwards is picked up by the next
    // poll of that charge id. Closing this dialog is not a refund.
    if (id && phaseRef.current === 'waiting') {
      try {
        await gatewayPaymentAPI.cancelCharge(id);
      } catch {
        /* closing the dialog is more important than recording the cancel */
      }
    }
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && phase !== 'creating') void cancel();
      }}
    >
      <div
        className="appt-dialog appt-card w-full max-w-md rounded-2xl border p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
          {gateway === 'tap'
            ? t({ en: 'Card payment', ar: 'الدفع بالبطاقة' })
            : t({ en: `Pay with ${PAYMENT_GATEWAYS[gateway].nameEn}`, ar: `الدفع عبر ${PAYMENT_GATEWAYS[gateway].nameAr}` })}
        </h2>
        <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {depositCredit > 0
            ? t({
                en: `${fmtMoney(total)} invoice · ${fmtMoney(depositCredit)} deposit already paid`,
                ar: `فاتورة ${fmtMoney(total)} · عربون مدفوع ${fmtMoney(depositCredit)}`,
              })
            : t({ en: 'Charge the customer’s card', ar: 'تحصيل بطاقة العميل' })}
        </div>

        {phase === 'creating' && (
          <p className="mt-6 text-center text-sm text-slate-500">
            {t({ en: 'Opening the payment…', ar: 'جارٍ فتح الدفع…' })}
          </p>
        )}

        {phase === 'waiting' && charge && (
          <div className="mt-5 text-center">
            <div className="text-2xl font-bold tabular-nums text-slate-800 dark:text-slate-100">
              {fmtMoney(charge.amount)}
            </div>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t({
                en: 'Ask the customer to scan and pay. This updates on its own.',
                ar: 'اطلب من العميل المسح والدفع. سيتم التحديث تلقائيًا.',
              })}
            </p>
            {qrDataUrl && (
              <img
                src={qrDataUrl}
                alt={t({ en: 'Payment QR code', ar: 'رمز الدفع' })}
                className="mx-auto mt-4 h-56 w-56 rounded-lg bg-white p-2"
              />
            )}
            <a
              className="mt-3 inline-block text-sm font-semibold text-blue-600 underline dark:text-blue-400"
              href={charge.redirect_url}
              target="_blank"
              rel="noreferrer"
            >
              {t({ en: 'Open the payment page', ar: 'فتح صفحة الدفع' })}
            </a>
            <div className="mt-4 text-xs text-slate-400">
              {t({ en: 'Waiting for the gateway', ar: 'في انتظار بوابة الدفع' })}
              {polls > 0 ? ` · ${polls}` : ''}
            </div>
          </div>
        )}

        {phase === 'succeeded' && (
          <p className="mt-6 text-center text-sm font-semibold text-emerald-600">
            {t({ en: 'Paid. Writing the invoice…', ar: 'تم الدفع. جارٍ إصدار الفاتورة…' })}
          </p>
        )}

        {error && (
          <div className="mt-5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
            onClick={() => void cancel()}
            disabled={phase === 'creating' || phase === 'succeeded'}
          >
            {phase === 'failed'
              ? t({ en: 'Close', ar: 'إغلاق' })
              : t({ en: 'Cancel', ar: 'إلغاء' })}
          </button>
        </div>
      </div>
    </div>
  );
}
